/* eslint-disable no-lone-blocks */
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useDragLayer, useDrop } from 'react-dnd';
import CustomDragLayer from './CustomDragLayer';
import {
  ReactFlow,
  applyNodeChanges,
  Background,
  Controls,
  type Node,
  type Edge,
  type OnNodesChange,
  useReactFlow,
  MarkerType,
  MiniMap,
  CoordinateExtent,
  useUpdateNodeInternals,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import {
  addInitialCalculationFlow,
  addResultantDatatypeInCalculationFlow,
  addSourceInfoInCalculationFlow,
  deleteFlow,
  setActiveFlow,
  setFlowPositions,
  setResultTypeForIfElse,
  updateFlowDisplayName,
  updateFlowPositions,
} from '../../../redux/Calculations/CalculationsActions';
import { useDispatch } from 'react-redux';
import TestOrCloseFomula from '../BottomMenu/TestOrCloseFormula';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { getFlowTypeFromFunctionName } from '../utils';
import { fontSize, palette } from '../../..';
import CalcFlowNode from './CalcFlowNode';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { useFitViewIfNodesHidden } from '../../../hooks/useFitViewIfNodesHidden';
import { useEnsureNodeVisible } from '../../../hooks/useEnsureNodeVisible';

// types of node.data
export type flowNodeData = {
  parentRect: { width: number, height: number };
  indexKey: number;
  flowUID: string;
  propKey: string;

  deleteFlow: (propKey: string, flowUID: string) => void;
  setFlowDisplayNames: (propKey: string, flowUID: string, displayName: string) => void;
};
// types of node
export type flowNode = Node<flowNodeData, 'flowNode'>;

// helper to get nearest starting and ending point based on the position
const getHandleDirection = (
  sourcePos: { x: number; y: number },
  targetPos: { x: number; y: number },
) => {
  const dx = targetPos.x - sourcePos.x;
  const dy = targetPos.y - sourcePos.y;

  const isHorizontal = Math.abs(dx) > Math.abs(dy);

  if (isHorizontal) {
    // LEFT ↔ RIGHT
    return {
      sourceHandle: dx > 0 ? 'right' : 'left',
      targetHandle: dx > 0 ? 'left' : 'right',
    };
  } else {
    // TOP ↔ BOTTOM
    return {
      sourceHandle: dy > 0 ? 'bottom' : 'top',
      targetHandle: dy > 0 ? 'top' : 'bottom',
    };
  }
};

// this will create arrow
const buildEdge = (
  source: string,
  target: string,
  sourcePos: { x: number; y: number },
  targetPos: { x: number; y: number },
  extra?: Partial<Edge>,
): Edge => {
  const { sourceHandle, targetHandle } = getHandleDirection(sourcePos, targetPos);
  return {
    id: `${source}-${target}`,
    source,
    target,
    sourceHandle,
    targetHandle,
    type: 'default',
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: '#af99db',
    },
    animated: false,
    style: { stroke: '#af99db', strokeWidth: 2 },

    ...extra,
  };
};

const nodeTypes = {
  flowNode: CalcFlowNode,
};

const CalculationCanvas = ({ propKey }: { propKey: string }) => {
  const { flows, flowIds, conditionFilters, calculations } = useSelector((state: RootState) => {
    const flows =
      state.calculations.properties[propKey]?.currentCalculationSession?.calculationInfo.flows ??
      {};
    return {
      calculations: state.calculations ?? {},
      flowIds: Object.keys(flows) ?? [],
      flows,
      conditionFilters:
        state.calculations.properties[propKey]?.currentCalculationSession?.calculationInfo
          .conditionFilters ?? {},
    };
  });
  const { screenToFlowPosition, fitView, setViewport, getViewport, getNode } = useReactFlow();
  const currentCalculationSession =
    calculations?.properties[propKey]?.currentCalculationSession ?? null;

  let flowPositions = currentCalculationSession?.flowPositions;


  const conditionsLength = Object.keys(
    currentCalculationSession?.calculationInfo?.conditionFilters ?? {},
  ).length;
  const [alert, setAlert] = useState<any>(null);
  const [parentRect, setParentRect] = useState({ width: 0, height: 0 });
  // console.log(parentRect)
  const dispatch = useDispatch();
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const updateNodeInternals = useUpdateNodeInternals()
  const isAddingNodeRef = useRef(false);
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem() as { uid?: string },
    currentOffset: monitor.getClientOffset(),
  }));
  const fitIfHidden = useFitViewIfNodesHidden()
  const ensureNodeVisible = useEnsureNodeVisible()

  const _updateFlowDisplayName = (propKey: string, flowUID: string, displayName: string) =>
    dispatch(updateFlowDisplayName(propKey, flowUID, displayName));

  const _deleteFlow = (propKey: string, flowUID: string) => {
    dispatch(deleteFlow(propKey, flowUID));

    // deleting the node from local state also
    setNodes((prev) => prev.filter((node) => node.id !== flowUID));
  };

  const updateFlowPosition = (propKey: string, calculationFlowUID: string, x: string, y: string) =>
    dispatch(updateFlowPositions(propKey, calculationFlowUID, x, y));

  useEffect(() => {
    if (!currentCalculationSession) return;
    flowPositions = currentCalculationSession.flowPositions;
  }, [currentCalculationSession?.flowPositions]);

  useEffect(() => {
    if (!parentRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          setParentRect({ width: entry.contentRect.width, height: entry.contentRect.height });
        }
      }
    });

    observer.observe(parentRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const activeFlow = currentCalculationSession?.activeFlow;
  const getIsAggregation = (name: string): boolean => {
    /**
     * include all the flow names  in the array that only supports row level calculations
     */
    if (
      [
        'String to Date',
        'Date Add',
        'Date Difference',
        'Date Name',
        'Date Number',
        'Date Truncate',
        'Today',
        'Now',
        'IfElse',
        'Lowercase',
        'Uppercase',
        'Propercase',
        'Trim',
        'Substring Left',
        'Substring Right',
        'Length',
        'Left Trim',
        'Right Trim',
        'Replace',
        'Split',
        'Min',
        'Max',
        'Concatenation',
        'Type Conversion',
      ].includes(name)
    )
      return false;
    return true;
  };

  const handleNewFlowCreation = (item: any, x: number, y: number) => {
    const keys = Object.keys(flows);

    const newFlowId =
      keys.length === 0
        ? 'f1'
        : 'f' + (Math.max(...keys.map((k) => Number(k.replace('f', '')))) + 1);

    dispatch(
      addInitialCalculationFlow(
        propKey,
        {
          uid: newFlowId,
          flow: item.flowName ?? item.name,
          isAggregation: getIsAggregation(item.name),
        },
        `flt${conditionsLength + 1}`,
        item.name === 'IfElse' ? 'If Else' : item.name,
      ),
    );

    dispatch(setFlowPositions(propKey, newFlowId, x.toString(), y.toString()));

    if (item.name === 'IfElse') {
      dispatch(setResultTypeForIfElse(propKey, newFlowId, null));
    }

    switch (item.flowName ? item.flowName : item.name) {
      case 'log':
        dispatch(
          addSourceInfoInCalculationFlow(
            10,
            {
              uid: newFlowId,
              flow: 'log',
            },
            propKey,
            'decimal',
            false,
            'sum',
            0,
          ),
        );
        break;

      case 'replace':
        dispatch(
          addSourceInfoInCalculationFlow(
            '',
            {
              uid: newFlowId,
              flow: 'replace',
            },
            propKey,
            'text',
            false,
            null,
            0,
          ),
        );

        dispatch(
          addSourceInfoInCalculationFlow(
            '',
            {
              uid: newFlowId,
              flow: 'replace',
            },
            propKey,
            'text',
            false,
            null,
            0,
          ),
        );

        break;

      case 'split': {
        dispatch(
          addSourceInfoInCalculationFlow(
            '',
            {
              uid: newFlowId,
              flow: 'split',
            },
            propKey,
            'text',
            false,
            null,
            0,
          ),
        );

        dispatch(
          addSourceInfoInCalculationFlow(
            1,
            {
              uid: newFlowId,
              flow: 'split',
            },
            propKey,
            'decimal',
            false,
            null,
            0,
          ),
        );

        dispatch(
          addSourceInfoInCalculationFlow(
            'left',
            {
              uid: newFlowId,
              flow: 'split',
            },
            propKey,
            'text',
            false,
            null,
            0,
          ),
        );

        break;
      }

      case 'substringleft': {
        dispatch(
          addSourceInfoInCalculationFlow(
            2,
            {
              uid: newFlowId,
              flow: 'substringleft',
            },
            propKey,
            'decimal',
            false,
            null,
            0,
          ),
        );

        dispatch(
          addSourceInfoInCalculationFlow(
            'include',
            {
              uid: newFlowId,
              flow: 'substringleft',
            },
            propKey,
            'text',
            false,
            null,
            0,
          ),
        );

        break;
      }

      case 'substringright': {
        dispatch(
          addSourceInfoInCalculationFlow(
            2,
            {
              uid: newFlowId,
              flow: 'substringright',
            },
            propKey,
            'decimal',
            false,
            null,
            0,
          ),
        );

        dispatch(
          addSourceInfoInCalculationFlow(
            'include',
            {
              uid: newFlowId,
              flow: 'substringright',
            },
            propKey,
            'text',
            false,
            null,
            0,
          ),
        );

        break;
      }

      default:
        break;
    }
  };

  const parentRef = useRef<HTMLDivElement | null>(null);
  const [, dropRef] = useDrop({
    accept: ['calculationSelected', 'calculationPicked'],
    drop: (item: any, monitor) => {
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      // Convert screen pointer → ReactFlow coordinates
      const position = screenToFlowPosition(clientOffset);

      if (!position) {
        return;
      }
      if (!parentRef.current) {
        return;
      }

      if (item.uid in flows) {
        updateFlowPosition(propKey, item.uid, position.x.toString(), position.y.toString());
        return;
      }

      if (Object.keys(flows).length === 10) {
        setAlert({
          severity: 'warning',
          message: 'You can not add new flow Max 10 is allowed',
        });
        return;
      }

      handleNewFlowCreation(item, position.x, position.y);
    },
  });

  const getResultantType = (
    types: string[],
    operation: string,
  ): 'integer' | 'decimal' | '' | 'text' | 'string' | 'date' | 'timestamp' => {
    if (
      [
        'addition',
        'subtraction',
        'multiplication',
        'division',
        'absolute',
        'ceiling',
        'floor',
        'log',
        'min',
        'max',
      ].includes(operation)
    ) {
      if (types.includes('decimal')) return 'decimal';
      return 'integer';
    }
    if (
      [
        'lowercase',
        'uppercase',
        'propercase',
        'trim',
        'substringright',
        'substringleft',
        'ltrim',
        'rtrim',
        'replace',
        'split',
        'concat',
      ].includes(operation)
    ) {
      if (types.includes('string')) return 'string';
      if (types.includes('text')) return 'text';
    }

    if (
      ['stringToDate', 'truncateDate', 'minDate', 'maxDate'].includes(operation)
    ) {
      return 'date';
    }
    if ('addDateInterval' === operation) return (
      (types[0] as 'integer' | 'decimal' | '' | 'text' | 'string' | 'date' | 'timestamp') ?? 'date'
    );
    if (['datePartName'].includes(operation)) {
      if (types.includes('string')) return 'string';
      if (types.includes('text')) return 'text';
      return 'string';
    }
    if (['datePartNumber', 'dateInterval', 'length'].includes(operation)) {
      return 'integer';
    }
    if (['IfElse'].includes(operation)) {
      if (types.includes('decimal')) return 'decimal';
      if (types.includes('integer')) return 'integer';
      if (types.includes('string')) return 'string';
      if (types.includes('text')) return 'text';
      if (types.includes('date')) return 'date';
      if (types.includes('timestamp')) return 'timestamp';
    }
    return '';
  };

  const calculateResultantType = (flowUID: string) => {
    if (!currentCalculationSession) return '';
    const flowData = flows[flowUID][0];
    const flowName = flowData.flow;
    const sourceTypes: string[] = [];
    if (['summarize', 'typeCast','power'].includes(flowName)) return flowData.resultantDatatype;

    if (flowName === 'log' && flowData.sourceType) {
      if (flowData.sourceType.length === 1) {
        return '';
      } else {
        return getResultantType(flowData.sourceType, 'log');
      }
    }
    const allowedFlowNames = [
      'lowercase',
      'uppercase',
      'propercase',
      'trim',
      'substringright',
      'substringleft',
      'length',
      'ltrim',
      'rtrim',
      'replace',
      'split',
      'dateInterval',
      'concat',
    ];

    if (flowData.sourceType) {
      flowData.sourceType.forEach((sourceID: string) => {
        if (sourceID !== 'field' && sourceID !== 'flow' && allowedFlowNames.includes(flowName)) {
          return '';
        }
        if (sourceID === 'field') {
          const fieldDataTypes = flowData.source
            .map((sourceId: string) => {
              const field = currentCalculationSession.calculationInfo.fields[sourceId];
              return field?.dataType;
            })
            .filter(
              (
                dataType: string | undefined,
              ): dataType is 'text' | 'integer' | 'decimal' | 'date' | 'timestamp' =>
                ['text', 'integer', 'decimal', 'date', 'timestamp'].includes(dataType as string),
            );
          sourceTypes.push(...fieldDataTypes);
        } else if (sourceID === 'flow') {
          flowData.source.forEach((sourceFlowID: string) => {
            if (flows[sourceFlowID]) {
              const sourceFlowData = flows[sourceFlowID][0];
              if (sourceFlowData.resultantDatatype) {
                sourceTypes.push(sourceFlowData.resultantDatatype);
              }
            }
          });
        } else {
          sourceTypes.push(sourceID);
        }
      });
    }
    if (sourceTypes.length === 0) {
      return '';
    }
    return getResultantType(sourceTypes, flowName);
  };

  const arrowsList = useMemo(() => {
    const allArrows: { id: string; source: string; target: string }[] = [];
    for (const flowUID of flowIds) {
      const flowDataArr = flows[flowUID] || [];
      for (const flowData of flowDataArr) {
        if (flowData.sourceType) {
          flowData.sourceType.forEach((sourceID: string, index: number) => {
            if (sourceID === 'flow') {
              allArrows.push({
                id: `${flowData.source[index]}-${flowUID}`,
                source: flowData.source[index],
                target: flowUID,
              });
            }
          });
        }
        const flowFilter = flowData.filter;
        if (flowFilter) {
          const conditions = conditionFilters[flowFilter]?.[0]?.conditions || [];
          for (const condition of conditions) {
            if (condition.leftOperandType[0] === 'flow') {
              allArrows.push({
                id: `${condition.leftOperand[0]}-${flowUID}`,
                source: condition.leftOperand[0],
                target: flowUID,
              });
            }
            if (condition.rightOperandType && condition.rightOperandType[0] === 'flow') {
              allArrows.push({
                id: `${condition.rightOperand[0]}-${flowUID}`,
                source: condition.rightOperand[0] as string,
                target: flowUID,
              });
            }
            if (condition.rightOperandType && condition.rightOperandType[1] === 'flow') {
              allArrows.push({
                id: `${condition.rightOperand[1]}-${flowUID}`,
                source: condition.rightOperand[1] as string,
                target: flowUID,
              });
            }
          }
        }
      }
    }
    return allArrows;
  }, [flows, flowIds, conditionFilters]);

  useEffect(() => {
    Object.keys(flows).forEach((flowUID: string) => {
      const flowData = flows[flowUID][0];
      const flowName = flowData?.flow;
      const newResultantDatatype = calculateResultantType(flowUID);
      const existingResultantDatatype = flowData.resultantDatatype;

      if (!newResultantDatatype || newResultantDatatype === existingResultantDatatype) return;
      
      dispatch(
        addResultantDatatypeInCalculationFlow(
          { uid: flowUID, flow: flowName },
          propKey,
          newResultantDatatype,
          0,
        ),
      );
    });
  }, [flows, propKey]);

  // react flow handler and events

  const computedNodes: Node<flowNodeData>[] = useMemo(() => {
    if (!flowPositions) return [];
    const localFlowPositions = flowPositions;
    return Object.keys(flows)
      .filter((flowUID) => {
        const flowName = flows[flowUID]?.[0]?.flow;
        return Boolean(getFlowTypeFromFunctionName(flowName));
      })
      .map((flowUID, index) => ({
        id: flowUID,
        type: 'flowNode',
        position: {
          x: Number(localFlowPositions[flowUID]?.x ?? 0),
          y: Number(localFlowPositions[flowUID]?.y ?? index * 120),
        },
        dragHandle: '.calculation_flow',
        zIndex: activeFlow === flowUID ? 100 : index + 1,
        data: {
          parentRect,
          indexKey: index,
          flowUID,
          propKey,
          deleteFlow: _deleteFlow,
          setFlowDisplayNames: _updateFlowDisplayName
        },
      }));
  }, [flows, flowPositions, parentRect, propKey, activeFlow]);

  const computedEdges: Edge[] = useMemo(() => {
    if (!flowPositions) return [];
    const localFlowPositions = flowPositions;
    const results: Edge[] =
      arrowsList?.map((arrow) => {
        const { source, target } = arrow;
        const targetPos = {
          x: Number(localFlowPositions[target]?.x ?? 0),
          y: Number(localFlowPositions[target]?.y ?? 0),
        };
        const sourcePos = {
          x: Number(localFlowPositions[source]?.x ?? 0),
          y: Number(localFlowPositions[source]?.y ?? 0),
        };

        const edge = buildEdge(source, target, sourcePos, targetPos);
        return edge;
      }) ?? [];
    return results;
  }, [arrowsList]);

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [setNodes],
  );

  // to update node position while node draging
  const onNodeDragStop = useCallback((_: React.MouseEvent, node: Node) => {
    let x = node.position.x.toString();
    let y = node.position.y.toString();
    updateFlowPosition(propKey, node.id, x, y);
  }, []);

  // this will update node position while react dnd dragging and also handle canvas scroll while dragging
  const PAN_MARGIN = 40;
  const PAN_SPEED = 15;

  useEffect(() => {
    if (!isDragging || !currentOffset || !item?.uid || !parentRef.current) return;

    const { x, y } = currentOffset;
    const rect = parentRef.current.getBoundingClientRect();

    let dx = 0;
    let dy = 0;

    // Left / Right
    if (x < rect.left + PAN_MARGIN) dx = PAN_SPEED;
    else if (x > rect.right - PAN_MARGIN) dx = -PAN_SPEED;

    // Top / Bottom
    if (y < rect.top + PAN_MARGIN) dy = PAN_SPEED;
    else if (y > rect.bottom - PAN_MARGIN) dy = -PAN_SPEED;

    if (dx || dy) {
      const viewport = getViewport();

      setViewport(
        {
          x: viewport.x + dx,
          y: viewport.y + dy,
          zoom: viewport.zoom,
        },
        { duration: 0 }
      );
    }

    const pos = screenToFlowPosition({
      x: currentOffset.x,
      y: currentOffset.y,
    });

    // update nodes
    setNodes((nodes) =>
      nodes.map((n, idx) =>
        n.id === item.uid
          ? {
            ...n, position: pos, zIndex: 1000, style: {
              ...n.style,
              pointerEvents: 'none',
            },
          }
          : { ...n, zIndex: idx + 1 }
      )
    );

    updateNodeInternals(item.uid);
  }, [isDragging, currentOffset, item?.uid]);

  useEffect(() => {
    setNodes(computedNodes);
  }, [computedNodes]);

  useEffect(() => {
    setEdges(computedEdges);
  }, [computedEdges]);

  useEffect(() => {
    if (currentCalculationSession?.uuid) {
      dispatch(setActiveFlow(propKey, null));
      fitView();
    }
  }, []);

  // only run when new node added and it will check if any node are hidden after adding it then trigger fit view
  useEffect(() => {
    fitIfHidden(nodes, parentRect.width, parentRect.height);
  }, [nodes.length]);

  // to fit active node
  useEffect(() => {
    if (!activeFlow) return
    if (!nodes.some(n => n.id === activeFlow)) return;
    const flowData = flows[activeFlow][0];
    const flowName = flowData.flow;
    // Skip if this was triggered by node addition
    if (isAddingNodeRef.current) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (flowName === "summarize") {
          ensureNodeVisible(activeFlow, false);
        }
        else {
          ensureNodeVisible(activeFlow, true);
        }
      });
    });

  }, [activeFlow]);

  // for preventing the infinite drag in the canvas
  const nodeBounds: CoordinateExtent = [
    [-1900, -1000], //minX and minY
    [1950, 1110], //maxX and maxY
  ];

  const translateBounds: CoordinateExtent = [
    [-1930, -1020], // extra space for camera movement
    [1980, 1130],
  ];
  if (!currentCalculationSession) return null;
  return (
    <div
      ref={(el) => {
        dropRef(el);
        parentRef.current = el;
      }}
      onClick={(e) => {
        setActiveFlow(propKey, null);
      }}
      style={{
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          fontSize: fontSize.large,
          fontWeight: 'bold',
          color: palette.primary.contrastText,
          position: 'absolute',
          left: '50%',
          top: 5,
          zIndex: 1000,
        }}
      >
        {currentCalculationSession?.uuid ? 'Edit Formula' : 'Formula Builder'}
      </span>
      <CustomDragLayer />
      {/* TODO: This will break with if else reason being flowUID[0 won't always work here] */}
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
      <ReactFlow
        minZoom={0.4}
        maxZoom={1.4}
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        zoomOnScroll={false}
        onNodeDragStop={onNodeDragStop}
        nodesFocusable={true}
        elementsSelectable={true}
        // fitView
        // nodeExtent={nodeBounds}
        // translateExtent={translateBounds}
        fitViewOptions={{ padding: 0.2 }}
        style={{ paddingTop: 5 }}
      >
        <Background />
        <Controls />
        <MiniMap
          position="top-right"
          pannable
          zoomable
          style={{ width: 120, height: nodes.length === 0 ? 0 : 100 }}
        />
      </ReactFlow>

      {calculations.properties[propKey]?.currentCalculationSession && <TestOrCloseFomula />}
    </div>
  );
};

export default CalculationCanvas;